import { useQuery } from "@msuite/ui/query";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { responseHandler } from "@msuite/ui/utils";
import { client } from "@/hono";

/** Props Interface */
interface WikiImageBlockProps {
	fileId: string;
}

/**
 * The picture an `imageBlock` embeds: the file's signed URL, fetched by id and shared with every
 * other place that shows the file. The stored width and height hold the block's shape while the
 * bytes load, so the text beneath does not move when they arrive. A file that is gone says so in
 * place, the way a deleted video's block does, until the next save prunes the block.
 */
export function WikiImageBlock({ fileId }: WikiImageBlockProps) {
	/** Hooks */
	const { t } = useTranslation();

	/** Query */
	const { data, isLoading, isError } = useQuery({
		queryKey: ["remote-file-url", fileId],
		queryFn: async () => {
			const response = await client.files[":fileId"].$get({ param: { fileId } });
			const result = await responseHandler(response);
			return { signedUrl: result.signedUrl ?? null, file: result.file };
		},
		staleTime: 1000 * 60 * 60,
	});

	/** Computed */
	const width = data?.file?.width ?? null;
	const height = data?.file?.height ?? null;
	const aspectRatio = width && height ? `${width} / ${height}` : undefined;

	/** Guard */
	if (isLoading) {
		return (
			<div
				className="card w-full max-w-[640px] animate-pulse bg-primaryGray-100 dark:bg-primaryGray-800"
				style={{ aspectRatio: aspectRatio ?? "16 / 9" }}
			/>
		);
	}

	if (isError || !data?.signedUrl) {
		return (
			<div className="card flex items-center gap-2 p-4 opacity-70">
				<Icon name="ImageOff" />
				<span>{t("images.not_found")}</span>
			</div>
		);
	}

	/** Render */
	return (
		<img
			src={data.signedUrl}
			alt={data.file?.fileName ?? ""}
			width={width ?? undefined}
			height={height ?? undefined}
			className="h-auto max-w-full rounded-base"
			style={{ aspectRatio }}
		/>
	);
}
